import React, { useState, useEffect } from "react";
import "./ShareTripModal.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShareTripModal = ({
  
  open,
  onClose,
  tripId,
  sharedUsers
}) => {
  // On open, initialize emails with already shared users
  const [emails, setEmails] = useState([{ value: '', error: '', userId: null }]);
  const [saveError, setSaveError] = useState("");

  const loadSharedUsers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/sharedTrips/${tripId}`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" }
      });
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        setEmails(
          data.map(u => ({
            value: u.email,
            error: '',
            userId: u.userId || u._id || null
          }))
          // Add an empty row for new input
          .concat([{ value: '', error: '', userId: null }])
        );
      } else {
        setEmails([{ value: '', error: '', userId: null }]);
      }
    } catch {
      setEmails([{ value: '', error: '', userId: null }]);
    }
    setSaveError("");
  };

  // Run loadSharedUsers every time the modal is opened
  useEffect(() => {
    if (open) {
      loadSharedUsers();
    }
  }, [open, tripId]);

  if (!open) return null;

  // Handler for adding a new email row
  const handleAddRow = async (idx) => {
    const email = emails[idx].value.trim();
    if (!email) {
      updateEmailError(idx, "Email required");
      return;
    }
    // Prevent duplicate in the edit list
    if (emails.some((row, i) => i !== idx && row.value.trim() === email)) {
      updateEmailError(idx, "Already in list");
      return;
    }
    // Check with backend if user exists
    try {
      const res = await fetch(`${BACKEND_URL}/check-user-exists`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      // Accept either {exists: true, userId} or {userExists: { _id: ... }}
      let userId = null;
      if (data.userExists && data.userExists._id) {
        userId = data.userExists._id;
      } else if (data.exists && data.userId) {
        userId = data.userId;
      }
      if (!userId) {
        updateEmailError(idx, "User does not exist");
        return;
      }
      // Add a new empty row and clear error, store userId
      setEmails(prev =>
        prev.map((row, i) =>
          i === idx ? { value: email, error: '', userId } : row
        ).concat([{ value: '', error: '', userId: null }])
      );
    } catch (e) {
      updateEmailError(idx, "Server error");
    }
  };

  // Handler for removing a row
  const handleRemoveRow = (idx) => {
    const email = emails[idx].value.trim();
    
    setEmails(prev => prev.filter((_, i) => i !== idx));
  };

  // Handler for changing email input
  const handleChange = (idx, val) => {
    setEmails(prev =>
      prev.map((row, i) =>
        i === idx ? { ...row, value: val, error: '', userId: null } : row
      )
    );
  };

  // Helper to set error for a row
  const updateEmailError = (idx, msg) => {
    setEmails(prev =>
      prev.map((row, i) =>
        i === idx ? { ...row, error: msg } : row
      )
    );
  };

  // Find the first error to display globally
  const globalError = emails.find(row => row.error)?.error || saveError || "";

  // Handler for Save button
  const handleSave = async () => {
    setSaveError("");
    let updatedEmails = emails;

    // If the last row has input but no userId, validate and update it before saving
    const lastIdx = emails.length - 1;
    const lastRow = emails[lastIdx];
    if (
      lastRow &&
      lastRow.value.trim() &&
      !lastRow.error &&
      !lastRow.userId
    ) {
      try {
        const res = await fetch(`${BACKEND_URL}/check-user-exists`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: lastRow.value.trim() })
        });
        const data = await res.json();
        let userId = null;
        if (data.userExists && data.userExists._id) {
          userId = data.userExists._id;
        } else if (data.exists && data.userId) {
          userId = data.userId;
        }
        if (userId) {
          updatedEmails = emails.map((row, i) =>
            i === lastIdx ? { ...row, userId } : row
          );
        } else {
          setSaveError("Last email does not exist or is invalid.");
          return;
        }
      } catch (e) {
        setSaveError("Server error while validating last email.");
        return;
      }
    }

    // Filter out empty rows and rows with errors
    const sharedWidthArray = updatedEmails
      .filter(row => row.value.trim() && !row.error && row.userId)
      .map(row => ({ email: row.value.trim(), userId: row.userId }));

    try {
      const res = await fetch(`${BACKEND_URL}/updateSharedTrips/${tripId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sharedWidthArray })
      });
      if (res.ok) {
        onClose();
      } else {
        const data = await res.json().catch(() => ({}));
        setSaveError(data.error || "Failed to update shared users.");
      }
    } catch (e) {
      setSaveError("Server error while saving.");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        {/* Close X button in top right */}
        <button
          className="modal-x-btn"
          title="Close"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
        <h3>Share Trip</h3>
        <div>
          <label>Add or edit users to share with:</label>
          <div className="share-email-list">
            {emails.map((row, idx) => (
              <div className="share-email-row" key={idx}>
                <div className="share-email-input-wrap">
                  <input
                    type="email"
                    value={row.value}
                    onChange={e => handleChange(idx, e.target.value)}
                    placeholder="Enter user email"
                    autoComplete="off"
                    className="share-email-input"
                  />
                  {idx === emails.length - 1 ? (
                    <button
                      className="share-add-btn"
                      title="Add"
                      type="button"
                      onClick={() => handleAddRow(idx)}
                    >
                      +
                    </button>
                  ) : (
                    <button
                      className="share-remove-btn"
                      title="Remove"
                      type="button"
                      onClick={() => handleRemoveRow(idx)}
                    >
                      –
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Global error placeholder */}
        <div className="share-global-error-row">
          <span className="share-global-error">
            {globalError || <span style={{ visibility: "hidden" }}>.</span>}
          </span>
        </div>

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button className="modal-save-btn" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default ShareTripModal;